import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { getRoundTitle } from '../../../helpers/bracketData';

const FIT_EPS = 0.02;
const MIN_SCALE = 0.5;
const MAX_SCALE = 2;
const ZOOM_STEP = 0.2;
const SWIPE_MIN_PX = 36;
const SWIPE_MAX_PX = 64;
const SNAP_MS = 240;
const MIN_PAGER_HEIGHT = 240;

// One round per screen. RZPP still owns pinch/pan; at fit scale a horizontal
// swipe snaps the transform to the next/previous page. Connectors are a
// desktop-only concern here: only one round is readable at a time.
const MobileBracket = ({ rounds, renderMatch }) => {
  const [pageWidth, setPageWidth] = useState(0);
  const [pageHeight, setPageHeight] = useState(0);
  const [displayIndex, setDisplayIndex] = useState(0);

  const containerRef = useRef(null);
  const transformRef = useRef(null);
  const settledIndexRef = useRef(0);
  const displayIndexRef = useRef(0);
  const panStartRef = useRef(null);
  const programmaticUntilRef = useRef(0);

  const maxIndex = Math.max(rounds.length - 1, 0);

  const clampIndex = useCallback(
    (index) => Math.min(Math.max(index, 0), maxIndex),
    [maxIndex]
  );

  const syncDisplay = useCallback((index) => {
    const clamped = Math.min(Math.max(index, 0), Math.max(maxIndex, 0));
    displayIndexRef.current = clamped;
    setDisplayIndex((current) => (current === clamped ? current : clamped));
    return clamped;
  }, [maxIndex]);

  const goToRound = useCallback(
    (index, { positionY = 0 } = {}) => {
      if (!pageWidth) {
        return;
      }
      const clamped = clampIndex(index);
      settledIndexRef.current = clamped;
      syncDisplay(clamped);
      programmaticUntilRef.current = Date.now() + SNAP_MS + 50;
      transformRef.current?.setTransform(
        -clamped * pageWidth,
        positionY,
        1,
        SNAP_MS,
        'easeOut'
      );
    },
    [clampIndex, pageWidth, syncDisplay]
  );

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    let frame;
    const measure = () => {
      if (frame) {
        cancelAnimationFrame(frame);
      }
      frame = requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        const width = Math.floor(rect.width);
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const height = Math.max(
          MIN_PAGER_HEIGHT,
          Math.floor(viewportHeight - rect.top - 12)
        );

        if (width > 0) {
          setPageWidth((current) => (current === width ? current : width));
        }
        setPageHeight((current) => (current === height ? current : height));
      });
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    window.visualViewport?.addEventListener('resize', measure);

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    observer?.observe(node);

    let cancelled = false;
    document.fonts?.ready.then(() => {
      if (!cancelled) {
        measure();
      }
    });

    return () => {
      cancelled = true;
      if (frame) {
        cancelAnimationFrame(frame);
      }
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (settledIndexRef.current > maxIndex) {
      goToRound(maxIndex);
    }
  }, [goToRound, maxIndex]);

  const handlePanningStart = (ref) => {
    panStartRef.current = {
      positionX: ref.state.positionX,
      positionY: ref.state.positionY,
      scale: ref.state.scale,
      settledIndex: settledIndexRef.current
    };
  };

  const handlePanningStop = (ref) => {
    const start = panStartRef.current;
    panStartRef.current = null;
    if (!start || !pageWidth) {
      return;
    }

    const { positionX, positionY, scale } = ref.state;
    const dx = positionX - start.positionX;
    const dy = positionY - start.positionY;

    if (Math.abs(scale - 1) > FIT_EPS) {
      // Zoomed in/out: free pan. Keep the title in step with the visible page.
      const centre = (pageWidth / 2 - positionX) / scale;
      const index = clampIndex(Math.floor(centre / pageWidth));
      settledIndexRef.current = index;
      syncDisplay(index);
      return;
    }

    const horizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
    const threshold = Math.min(SWIPE_MAX_PX, Math.max(SWIPE_MIN_PX, pageWidth * 0.12));

    if (horizontal && Math.abs(dx) >= threshold) {
      goToRound(start.settledIndex + (dx < 0 ? 1 : -1), { positionY });
      return;
    }

    // Not a page swipe: settle X back without losing the vertical position.
    settledIndexRef.current = start.settledIndex;
    programmaticUntilRef.current = Date.now() + 230;
    ref.setTransform(-start.settledIndex * pageWidth, positionY, 1, 180, 'easeOut');
    syncDisplay(start.settledIndex);
  };

  const handlePinchingStop = (ref) => {
    if (!pageWidth) {
      return;
    }

    const { positionX, positionY, scale } = ref.state;
    if (Math.abs(scale - 1) <= FIT_EPS) {
      goToRound(displayIndexRef.current, { positionY });
      return;
    }

    const centre = (pageWidth / 2 - positionX) / scale;
    const index = clampIndex(Math.floor(centre / pageWidth));
    settledIndexRef.current = index;
    syncDisplay(index);
  };

  const handleTransformed = (ref, state) => {
    transformRef.current = ref;
    if (!pageWidth || Date.now() < programmaticUntilRef.current) {
      return;
    }

    let index;
    if (Math.abs(state.scale - 1) <= FIT_EPS) {
      index = Math.round(-state.positionX / pageWidth);
    } else {
      const centre = (pageWidth / 2 - state.positionX) / state.scale;
      index = Math.floor(centre / pageWidth);
    }

    const clamped = clampIndex(index);
    if (clamped !== displayIndexRef.current) {
      displayIndexRef.current = clamped;
      setDisplayIndex(clamped);
    }
    // Keep the settled page in step when the zoom returns to fit scale, so a
    // later pan snaps back to the round actually on screen. Never do this
    // mid-drag: panningStop pages from the index captured at pan start.
    if (Math.abs(state.scale - 1) <= FIT_EPS && !panStartRef.current) {
      settledIndexRef.current = clamped;
    }
  };

  if (!rounds.length) {
    return null;
  }

  const current = rounds[Math.min(displayIndex, rounds.length - 1)] || rounds[0];
  const canGoBack = displayIndex > 0;
  const canGoForward = displayIndex < rounds.length - 1;

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
        <IconButton
          aria-label="Previous round"
          onClick={() => goToRound(displayIndex - 1)}
          disabled={!canGoBack}
          sx={{ minWidth: 40, minHeight: 40 }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ minWidth: 0, textAlign: 'center' }}>
          <Typography variant="subtitle2" noWrap>
            {getRoundTitle(current.round, current.matches)}
          </Typography>
          <Typography variant="caption" color="text.secondary" aria-live="polite">
            {displayIndex + 1} / {rounds.length}
          </Typography>
        </Box>
        <IconButton
          aria-label="Next round"
          onClick={() => goToRound(displayIndex + 1)}
          disabled={!canGoForward}
          sx={{ minWidth: 40, minHeight: 40 }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: pageHeight || MIN_PAGER_HEIGHT,
          overflow: 'hidden',
          cursor: 'grab',
          userSelect: 'none',
          touchAction: 'none',
          overscrollBehaviorX: 'contain',
          overscrollBehaviorY: 'contain'
        }}>
        {pageWidth > 0 && pageHeight > 0 && (
          <TransformWrapper
            key={`pager-${pageWidth}`}
            minScale={MIN_SCALE}
            maxScale={MAX_SCALE}
            initialScale={1}
            initialPositionX={-settledIndexRef.current * pageWidth}
            initialPositionY={0}
            limitToBounds={false}
            centerOnInit={false}
            centerZoomedOut={false}
            alignmentAnimation={{ disabled: true }}
            wheel={{ step: ZOOM_STEP }}
            doubleClick={{ disabled: true }}
            panning={{ velocityDisabled: true }}
            onInit={(ref) => {
              transformRef.current = ref;
            }}
            onTransformed={handleTransformed}
            onPanningStart={handlePanningStart}
            onPanningStop={handlePanningStop}
            onPinchingStop={handlePinchingStop}>
            {() => (
              <TransformComponent
                wrapperStyle={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                <Box display="flex" sx={{ width: rounds.length * pageWidth }}>
                  {rounds.map((round) => {
                    const roundTitle = getRoundTitle(round.round, round.matches);

                    return (
                      <Box
                        key={round.round}
                        sx={{
                          width: pageWidth,
                          flex: `0 0 ${pageWidth}px`,
                          boxSizing: 'border-box',
                          px: 1.5,
                          // Leave room for the floating zoom controls over the
                          // last card's buttons.
                          pb: 8
                        }}>
                        <Stack spacing={2.5}>
                          {round.matches.map((match) => renderMatch(match, null, roundTitle))}
                        </Stack>
                      </Box>
                    );
                  })}
                </Box>
              </TransformComponent>
            )}
          </TransformWrapper>
        )}

        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            zIndex: 2,
            display: 'flex',
            p: 0.25
          }}>
          <IconButton
            aria-label="Zoom out"
            onClick={() => transformRef.current?.zoomOut(ZOOM_STEP, 200, 'easeOut')}
            sx={{ minWidth: 36, minHeight: 36 }}>
            <ZoomOutIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Reset zoom"
            onClick={() => goToRound(displayIndexRef.current)}
            sx={{ minWidth: 36, minHeight: 36 }}>
            <FitScreenIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="Zoom in"
            onClick={() => transformRef.current?.zoomIn(ZOOM_STEP, 200, 'easeOut')}
            sx={{ minWidth: 36, minHeight: 36 }}>
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Paper>
      </Box>
    </Box>
  );
};

export default MobileBracket;
